// External Packages
import { useState, useContext, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AiOutlinePlus } from "react-icons/ai";

// Internal Packages
import { DataContext } from "../../context/data-context";
import { AuthContext } from "../../context/auth-context";
import { addVehicle } from "../../api/add";
import { decodeVin } from "../../api/fetch";
import { toTitleCase } from "../../util/toTitleCase";
import { uploadImageToFirebase } from "../../util/upload-image-to-firebase";
import LoadingSpinner from "../UI/LoadingSpinner";

/**
 * * This Component renders a form to add a new vehicle
 */
export default function AddVehicle() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { fetchVehicles, brands } = useContext(DataContext);

  // Initialize Processing States
  const [loading, setLoading] = useState(false);
  const [warning, setWarning] = useState();

  // Vehicle Image States & Refs
  /** Refs are used for hidden inputs so that
   * clicking on the image will trigger the input */
  const [cornerImage, setCornerImage] = useState();
  const [frontImage, setFrontImage] = useState();
  const [backImage, setBackImage] = useState();
  const [sideImage, setSideImage] = useState();
  const frontRef = useRef();
  const backRef = useRef();
  const sideRef = useRef();
  const cornerRef = useRef();

  // ==================================== VIN INPUT HANDLER
  /** This function is used to decode the VIN using POSH's API
   * and set the resulting vehicle information in the form */
  const vinInputHandler = async (vin) => {
    try {
      // Check if VIN is complete
      if (vin.length !== 17) return;
      setWarning();
      setLoading(true);
      // Send request to decode VIN
      const response = await decodeVin(user, { vin: vin });
      // Extract Results
      const vehicleInfo = response.vehicleInfo;

      // Check if brand is added
      if (
        !brands.find(
          (brand) => brand.name.toLowerCase() === vehicleInfo.make.toLowerCase()
        )
      )
        throw new Error("Brand not found. Please add the brand first.");

      // Set Results
      document.getElementsByName("make")[0].value = toTitleCase(
        vehicleInfo.make
      );
      document.getElementsByName("model")[0].value = vehicleInfo.model;
      document.getElementsByName("year")[0].value = vehicleInfo.year;
      document.getElementsByName("trim")[0].value = vehicleInfo.trim;
      document.getElementsByName("seats")[0].value = vehicleInfo.seats;
      document.getElementsByName("doors")[0].value = vehicleInfo.doors;
      document.getElementsByName("drive")[0].value = vehicleInfo.drive;
      document.getElementsByName("fuel")[0].value = vehicleInfo.fuel;

      // Generate Vehicle ID with the following format
      // Make (initial), Model (initial), Year (full), VIN (last 4)
      const vehicleId = `${vehicleInfo.make[0]}${vehicleInfo.model[0]}${
        vehicleInfo.year
      }${vehicleInfo.vin.substring(10, 16)}`;
      // Set Vehicle ID
      document.getElementsByName("id")[0].value = vehicleId;
      setLoading(false);
    } catch (err) {
      console.log(err);
      setWarning(err.message);
      setLoading(false);
    }
  };

  // ==================================== CHOOSE IMAGE HANDLER
  /** This function uses a ref given to a hidden input to click on it */
  const chooseImageHandler = (input) => {
    input.current.click();
  };

  // ==================================== ON SELECT IMAGE
  /** A single function is used to set the selected image
   * after the file input is triggered based on
   * the name of the input. */
  const onSelectImage = (e) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = URL.createObjectURL(e.target.files[0]);
    switch (e.target.name) {
      case "corner":
        setCornerImage(file);
        break;
      case "front":
        setFrontImage(file);
        break;
      case "back":
        setBackImage(file);
        break;
      case "side":
        setSideImage(file);
        break;
      default:
        break;
    }
  };

  // ==================================== SUBMIT HANDLER
  const submitHandler = async (event) => {
    try {
      event.preventDefault();
      // Clear any previous warnings
      setWarning();
      setLoading(true);
      // Get the auto-generated vehicle ID
      const vehicleId = event.target.id.value;

      // Get images from the form
      const images = {
        front: event.target.front.files[0],
        back: event.target.back.files[0],
        side: event.target.side.files[0],
        corner: event.target.corner.files[0],
      };

      // Upload images and hold the URLs
      const imageUrls = {
        front: images.front
          ? await uploadImageToFirebase(
              `images/${vehicleId}/front`,
              "front",
              images.front,
              () => {}
            )
          : "",
        back: images.back
          ? await uploadImageToFirebase(
              `images/${vehicleId}/back`,
              "back",
              images.back,
              () => {}
            )
          : "",
        side: images.side
          ? await uploadImageToFirebase(
              `images/${vehicleId}/side`,
              "side",
              images.side,
              () => {}
            )
          : "",
        corner: images.corner
          ? await uploadImageToFirebase(
              `images/${vehicleId}/corner`,
              "corner",
              images.corner,
              () => {}
            )
          : "",
      };

      // Create a payload for all vehicle information
      const payload = {
        id: vehicleId,
        vin: event.target.vin.value,
        make: event.target.make.value,
        model: event.target.model.value,
        year: event.target.year.value,
        trim: event.target.trim.value,
        drive: event.target.drive.value,
        fuel: event.target.fuel.value,
        seats: event.target.seats.value,
        doors: event.target.doors.value,
        license: event.target.license.value.toUpperCase(),
        license_state: event.target.license_state.value.toUpperCase(),
        location: event.target.location.value,
        address: event.target.address.value,
        color: event.target.color.value,
        plan: {
          id:
            event.target.plan.value === "LUXURY"
              ? "0"
              : event.target.plan.value === "PLUSH"
              ? "1"
              : "2",
          name: event.target.plan.value,
        },
        rates: {
          daily: event.target.daily.value,
          monthly: event.target.monthly.value,
          weekly: event.target.weekly.value,
        },
        status: {
          available: event.target.available.value === "1",
        },
        images: {
          front: imageUrls.front,
          back: imageUrls.back,
          side: imageUrls.side,
          corner: imageUrls.corner,
        },
      };

      // Add the vehicle to the database
      const response = await addVehicle(user, payload);
      // Fetch the vehicles again to update the list
      fetchVehicles(() => {
        navigate("/vehicles/info?id=" + response.id);
      });

      setLoading(false);
    } catch (err) {
      console.log(err);
      setWarning(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="container grid">
      <p className="text-lg font-semibold text-white">Add Vehicle</p>
      <form onSubmit={submitHandler} className="grid gap-8">
        <div className="grid text-sm md:grid-cols-4 gap-x-8 gap-y-4 text-stone-400 md:grid">
          <input className="hidden" name="id" disabled required />
          <div className="grid">
            <label className="text-sm">VIN NUMBER:</label>
            <input
              className="form-input"
              name="vin"
              required
              onChange={(e) => vinInputHandler(e.target.value)}
            />
          </div>
          <div className="grid">
            <label className="text-sm">MAKE:</label>
            <select className="form-input" name="make" id="make" required>
              {brands &&
                brands.map((brand, idx) => (
                  <option key={idx} value={brand.name}>
                    {brand.name}
                  </option>
                ))}
            </select>
          </div>
          <div className="grid">
            <label className="text-sm">MODEL:</label>
            <input className="form-input" name="model" required />
          </div>
          <div className="grid">
            <label className="text-sm">YEAR:</label>
            <input className="form-input" type="number" name="year" required />
          </div>
          <div className="grid">
            <label className="text-sm">TRIM:</label>
            <input className="form-input" name="trim" required />
          </div>
          <div className="grid">
            <label className="text-sm">DRIVE:</label>
            <input className="form-input" name="drive" required />
          </div>
          <div className="grid">
            <label className="text-sm">FUEL TYPE:</label>
            <input className="form-input" name="fuel" required />
          </div>
          <div className="grid">
            <label className="text-sm">SEATS:</label>
            <input className="form-input" type="number" name="seats" required />
          </div>
          <div className="grid">
            <label className="text-sm">DOORS:</label>
            <input className="form-input" type="number" name="doors" required />
          </div>
          <div className="grid">
            <label className="text-sm">LICENSE NUMBER:</label>
            <input className="form-input" name="license" required />
          </div>
          <div className="grid">
            <label className="text-sm">LICENSE STATE:</label>
            <input
              className="form-input"
              name="license_state"
              required
              maxLength={2}
              placeholder="e.g. MA"
            />
          </div>
          <div className="grid">
            <label className="text-sm">LOCATION:</label>
            <select className="form-input" name="location" required>
              <option value="Dallas, TX">Dallas, TX</option>
              <option value="Boston, MA">Boston, MA</option>
            </select>
          </div>
          <div className="grid">
            <label className="text-sm">GARAGE ADDRESS:</label>
            <input className="form-input" name="address" required />
          </div>
          <div className="grid">
            <label className="text-sm">COLOR:</label>
            <input className="form-input" name="color" required />
          </div>
          <div className="grid">
            <label className="text-sm">PLAN:</label>
            <select
              className="form-input"
              defaultValue="LUXURY"
              required
              name="plan"
            >
              <option value="LUXURY">LUXURY</option>
              <option value="PLUSH">PLUSH</option>
              <option value="POSH">POSH</option>
            </select>
          </div>
          <div className="grid">
            <label className="text-sm">DAILY RATE:</label>
            <input className="form-input" type="number" name="daily" required />
          </div>
          <div className="grid">
            <label className="text-sm">WEEKLY RATE:</label>
            <input
              className="form-input"
              type="number"
              name="weekly"
              required
            />
          </div>
          <div className="grid">
            <label className="text-sm">MONTHLY RATE:</label>
            <input
              className="form-input"
              type="number"
              name="monthly"
              required
            />
          </div>
          <div className="grid">
            <label>VEHICLE AVAILABILITY:</label>
            <select className="form-input" defaultValue="1" name="available">
              <option value="1">Available</option>
              <option value="2">Not Available</option>
            </select>
          </div>
        </div>
        <div className="grid gap-8 text-sm md:grid-cols-4 text-stone-400">
          <div className="grid p-4 border rounded-lg cursor-pointer md:h-40 h-fit justify-items-center border-stone-400">
            <p>CORNER IMAGE:</p>
            <input
              className="hidden"
              type="file"
              name="corner"
              ref={cornerRef}
              onChange={onSelectImage}
            />
            {cornerImage ? (
              <img
                alt="Vehicle"
                onClick={() => chooseImageHandler(cornerRef)}
                className="object-fit"
                src={cornerImage}
              />
            ) : (
              <AiOutlinePlus
                className="w-10 h-10"
                onClick={() => chooseImageHandler(cornerRef)}
              />
            )}
          </div>
          <div className="grid p-4 border rounded-lg cursor-pointer md:h-40 h-fit justify-items-center border-stone-400">
            <p>FRONT IMAGE:</p>
            <input
              className="hidden"
              type="file"
              name="front"
              ref={frontRef}
              onChange={onSelectImage}
            />
            {frontImage ? (
              <img
                alt="Vehicle"
                onClick={() => chooseImageHandler(frontRef)}
                className="object-fit"
                src={frontImage}
              />
            ) : (
              <AiOutlinePlus
                className="w-10 h-10"
                onClick={() => chooseImageHandler(frontRef)}
              />
            )}
          </div>
          <div className="grid p-4 border rounded-lg cursor-pointer md:h-40 h-fit justify-items-center border-stone-400">
            <p>BACK IMAGE:</p>
            <input
              className="hidden"
              type="file"
              name="back"
              ref={backRef}
              onChange={onSelectImage}
            />
            {backImage ? (
              <img
                alt="Vehicle"
                onClick={() => chooseImageHandler(backRef)}
                className="object-fit"
                src={backImage}
              />
            ) : (
              <AiOutlinePlus
                className="w-10 h-10"
                onClick={() => chooseImageHandler(backRef)}
              />
            )}
          </div>
          <div className="grid p-4 border rounded-lg cursor-pointer md:h-40 h-fit justify-items-center border-stone-400">
            <p>SIDE IMAGE:</p>
            <input
              className="hidden"
              type="file"
              name="side"
              ref={sideRef}
              onChange={onSelectImage}
            />
            {sideImage ? (
              <img
                alt="Vehicle"
                onClick={() => chooseImageHandler(sideRef)}
                className="object-fit"
                src={sideImage}
              />
            ) : (
              <AiOutlinePlus
                className="w-10 h-10"
                onClick={() => chooseImageHandler(sideRef)}
              />
            )}
          </div>
        </div>
        <div className="flex items-center space-x-4">
          {warning && <p className="text-red-600">{warning}</p>}
          {loading ? (
            <LoadingSpinner />
          ) : (
            <button className="w-full btn-primary md:w-fit">Submit</button>
          )}
        </div>
      </form>
    </div>
  );
}
